import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { User, Mail, Save, Loader2, Shield, Zap, CreditCard as CreditCardIcon, Lock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);

  const hasPassword = !!user?.has_password;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/profile', { name });
      await checkAuth();
      toast.success('Profil mis a jour !');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setSavingPwd(true);
    try {
      await api.post('/auth/set-password', {
        new_password: newPassword,
        current_password: hasPassword ? currentPassword : undefined,
      });
      await checkAuth();
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(hasPassword ? 'Mot de passe modifie !' : 'Mot de passe defini ! Vous pouvez desormais vous connecter avec email + mot de passe.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="profile-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight mb-8">
        Mon Profil
      </h1>

      <div className="space-y-6">
        {/* Profile info */}
        <div className="card-surface rounded-lg p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-8 h-8 text-white/30" />
              )}
            </div>
            <div>
              <p className="text-white font-medium">{user?.name || 'Utilisateur'}</p>
              <p className="text-sm text-white/40">{user?.email}</p>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label className="block text-xs text-white/40 mb-2">Nom complet</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 input-dark rounded-lg text-sm"
                  data-testid="profile-name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-white/40 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full pl-10 pr-4 py-3 input-dark rounded-lg text-sm opacity-50 cursor-not-allowed"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="btn-accent text-sm px-6 py-3 flex items-center gap-2 disabled:opacity-50"
              data-testid="save-profile-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder
            </button>
          </form>
        </div>

        {/* Subscription info */}
        <div className="card-surface rounded-lg p-8">
          <h2 className="font-heading text-lg font-medium text-white mb-4 flex items-center gap-2">
            <CreditCardIcon className="w-5 h-5 text-[#FF3300]" /> Abonnement
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/[0.03] rounded-lg p-4">
              <p className="text-xs text-white/40 mb-1">Forfait</p>
              <p className="text-lg font-heading font-medium text-white capitalize flex items-center gap-2">
                {user?.plan_type === 'unlimited' ? (
                  <><span className="text-[#FFD700]">Unlimited VIP</span> <span className="text-[#FFD700]">★</span></>
                ) : (user?.plan_type || 'Gratuit')}
              </p>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-4">
              <p className="text-xs text-white/40 mb-1">Credits</p>
              <p className="text-lg font-heading font-medium text-white flex items-center gap-1">
                <Zap className="w-4 h-4 text-[#FF3300]" />
                {user?.plan_type === 'unlimited' ? '∞ Illimite' : (user?.credits_balance || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="card-surface rounded-lg p-8">
          <h2 className="font-heading text-lg font-medium text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#34C759]" /> Securite
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg">
              <div>
                <p className="text-sm text-white">Type d'authentification</p>
                <p className="text-xs text-white/30 mt-0.5">
                  {user?.auth_type === 'google' ? 'Google OAuth' : 'Email / Mot de passe'}
                  {user?.auth_type === 'google' && hasPassword && ' + Mot de passe'}
                </p>
              </div>
              <span className="badge-success text-xs px-2 py-1 rounded-full">Actif</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-white/[0.03] rounded-lg">
              <div>
                <p className="text-sm text-white">Cles API</p>
                <p className="text-xs text-white/30 mt-0.5">Chiffrement AES-256</p>
              </div>
              <span className="badge-success text-xs px-2 py-1 rounded-full">Chiffre</span>
            </div>
          </div>
        </div>

        {/* Password set/change */}
        <div className="card-surface rounded-lg p-8" data-testid="password-section">
          <h2 className="font-heading text-lg font-medium text-white mb-2 flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#FF3300]" />
            {hasPassword ? 'Modifier le mot de passe' : 'Definir un mot de passe'}
          </h2>
          <p className="text-xs text-white/40 mb-5">
            {hasPassword
              ? 'Changez votre mot de passe de connexion.'
              : 'Ajoutez un mot de passe pour vous connecter avec email + mot de passe (en plus de Google).'}
          </p>
          <form onSubmit={handleSetPassword} className="space-y-4">
            {hasPassword && (
              <div>
                <label className="block text-xs text-white/40 mb-2">Mot de passe actuel</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 input-dark rounded-lg text-sm"
                    data-testid="current-password-input"
                    required
                  />
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-white/40 mb-2">Nouveau mot de passe (8 caracteres min.)</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 input-dark rounded-lg text-sm"
                  data-testid="new-password-input"
                  required
                  minLength={8}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-2">Confirmer le nouveau mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 input-dark rounded-lg text-sm"
                  data-testid="confirm-password-input"
                  required
                  minLength={8}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={savingPwd}
              className="btn-accent text-sm px-6 py-3 flex items-center gap-2 disabled:opacity-50"
              data-testid="save-password-btn"
            >
              {savingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {hasPassword ? 'Modifier le mot de passe' : 'Definir le mot de passe'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ProfilePage component end
